import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, takeActionOnDevices } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { MobileDevice } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import { useUserSettings } from '../../../../utils/use-user-settings';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface DeleteUserDeviceDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail?: string;
  selectedDevices: MobileDevice[];
  changeLogRoles: string[];
  setSelectedDeviceIds: Dispatch<SetStateAction<string[]>>;
}

export default function DeleteUserDeviceDialog({
  open,
  handleClose,
  userEmail,
  selectedDevices,
  changeLogRoles,
  setSelectedDeviceIds,
}: DeleteUserDeviceDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { features } = useUserSettings();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      takeActionOnDevices('DELETE', selectedDevices, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedDevices.length > 1
            ? 'Devices deleted successfully!'
            : 'Device deleted successfully!',
      });

      if (isFeatureEnabled('indexMobileDevicesReact', features)) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.MobileDevicesIndexedPage],
        });
      } else if (isFeatureEnabled('showMobileManagementFilters', features)) {
        queryClient.setQueryData(
          [UserKeys.SearchedMobileDevices],
          (oldData: MobileDevice[]) => {
            if (oldData) {
              return oldData.filter(
                (device) =>
                  !selectedDevices.some(
                    (selectedDevice) =>
                      selectedDevice.deviceId === device.deviceId
                  )
              );
            }
          }
        );
      } else {
        queryClient.setQueryData(
          [UserKeys.Devices, userEmail],
          (oldData: MobileDevice[]) => {
            if (oldData) {
              return oldData.filter(
                (device) =>
                  !selectedDevices.some(
                    (selectedDevice) =>
                      selectedDevice.deviceId === device.deviceId
                  )
              );
            }
          }
        );
      }

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedDevices.length > 1
            ? 'There was an issue deleting these devices. Please refresh and try again.'
            : 'There was an issue deleting this device. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        userEmail
          ? analytics.user_management.user_details.devices.delete_mobile_device
          : analytics.devices.mobile_management.delete_mobile_device,
        userEmail
          ? {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.mobileDevices,
            }
          : {
              module: ModuleList.modules.devices.name,
              section: ModuleList.modules.devices.sections.mobileManagement,
            }
      );
      setSelectedDeviceIds([]);
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.CanDeleteMobile.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  if (!selectedDevices?.length) return null;

  const isBulk = selectedDevices.length > 1;

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={isBulk ? 'Delete Devices' : 'Delete Device'}
      dialogContent={
        <Box>
          <Typography>
            {isBulk ? (
              <>
                Delete{' '}
                <strong>{selectedDevices.length} selected devices</strong>?
              </>
            ) : (
              <>
                Delete{' '}
                <strong>{selectedDevices[0]?.model || 'Unknown Device'}</strong>{' '}
                from{' '}
                <strong>
                  {selectedDevices[0]?.email?.[0] || 'Unknown User'}
                </strong>
                's devices?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: isBulk ? 'Delete Devices' : 'Delete Device',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
